import React, { useState, useEffect } from "react";
import type { CSSProperties } from "react";
import { AdFormat, AdPlacement, Advertisement } from "@/types/advertising";
import { getPlacementSpec } from "@/components/pages/user/dashboard/properties/Tabs/ads/token/placementDimensions";
import { useAdTracking } from "@/hooks/useAdTracking";
import AdvertisingService from "@/services/advertising";
import { BNPModal } from "@/components/bnp-ui/overlays/BNPModal";

interface AdRendererProps {
  ad: Advertisement;
  placement: AdPlacement;
  className?: string;
}

export const AdRenderer: React.FC<AdRendererProps> = ({
  ad,
  placement,
  className = "",
}) => {
  const [modalOpened, setModalOpened] = useState(false);

  // Impression (IntersectionObserver) + click tracking against the shared
  // advertising analytics endpoints.
  const { adRef, handleClick, impressionTracked } = useAdTracking({
    adId: ad?.id ?? "",
    placement,
    targetUrl: ad?.targetUrl ?? "",
  });

  // Popups live inside a BNP modal, so their content is only mounted once
  // the modal opens. Record the impression the moment it is shown instead of
  // relying on the IntersectionObserver.
  const popupAdId = ad?.id;
  useEffect(() => {
    if (!popupAdId || !modalOpened || placement !== AdPlacement.POPUP) {
      return;
    }

    AdvertisingService.TrackImpression({
      adId: popupAdId,
      placement,
      pageUrl: typeof window !== "undefined" ? window.location.href : "",
      referrer: typeof document !== "undefined" ? document.referrer : undefined,
    }).catch(() => undefined);
  }, [modalOpened, placement, popupAdId]);

  // 60-second delay timer specifically for Popup ads
  useEffect(() => {
    if (
      placement !== AdPlacement.POPUP ||
      !ad?.isActive ||
      ad?.reviewStatus !== "approved"
    ) {
      return;
    }

    const timer = setTimeout(() => {
      setModalOpened(true);
    }, 60000); // 60 seconds

    return () => clearTimeout(timer);
  }, [placement, ad?.isActive, ad?.reviewStatus]);

  // General Guard Clause
  if (!ad?.isActive || ad?.reviewStatus !== "approved") {
    return null;
  }

  // Placement dimensions come from the centralized config (token/dimensions.json).
  // The ad is rendered inside a box that keeps the placement's aspect ratio so
  // it never looks stretched or squashed, while stretching to fill whatever
  // width its wrapper provides.
  const placementSpec = getPlacementSpec(placement);
  const frameStyle: CSSProperties = placementSpec
    ? {
        aspectRatio: `${placementSpec.width} / ${placementSpec.height}`,
      }
    : {};

  const handleAdClick = (event: React.MouseEvent) => {
    // Fire-and-forget click tracking; the anchor's native navigation continues.
    handleClick(event);
  };

  // 1. Shared Inner Ad Content Renderer
  const renderAdContent = () => {
    return (
      <a
        href={ad.targetUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block w-full h-full cursor-pointer group"
        onClick={handleAdClick}
      >
        {ad.format === AdFormat.VIDEO && ad.videoUrl ? (
          <video
            src={ad.videoUrl}
            className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-[1.01]"
            autoPlay
            muted
            loop
            playsInline
          />
        ) : ad.imageUrl ? (
          <img
            src={ad.imageUrl}
            alt={ad.altText || ad.title}
            className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-[1.01]"
            loading="lazy"
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full p-6 text-center">
            <span className="text-xs font-semibold tracking-wider text-teal-800/60 uppercase mb-1">
              Sponsored
            </span>
            <h4 className="text-sm font-bold text-teal-950 line-clamp-1">
              {ad.title}
            </h4>
            {ad.description && (
              <p className="text-xs text-teal-900/80 mt-1 line-clamp-2 max-w-md">
                {ad.description}
              </p>
            )}
          </div>
        )}

        {(ad.format === AdFormat.IMAGE || ad.format === AdFormat.VIDEO) && (
          <span className="absolute bottom-2 right-2 px-2 py-0.5 text-[9px] font-medium tracking-wide bg-white/80 hover:bg-white text-teal-950/80 rounded border border-teal-100/30 shadow-sm backdrop-blur-xs select-none">
            Sponsored
          </span>
        )}
      </a>
    );
  };

  // 2. If it is a POPUP, render inside the BNP modal instead of inline layout
  if (placement === AdPlacement.POPUP) {
    return (
      <BNPModal
        isOpen={modalOpened}
        onClose={() => setModalOpened(false)}
        maxWidth="max-w-3xl"
      >
        <div className="relative overflow-hidden rounded-3xl">
          {/* Close button */}
          <button
            type="button"
            onClick={() => setModalOpened(false)}
            aria-label="Close advertisement"
            className="absolute top-3 right-3 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-teal-50 text-teal-700 shadow-sm transition-colors hover:bg-teal-100"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          <div
            ref={adRef}
            data-ad-id={ad.id}
            data-tracked={impressionTracked}
            className="relative w-full overflow-hidden bg-gradient-to-br from-teal-50/30 to-teal-100/20"
            style={frameStyle}
          >
            {renderAdContent()}
          </div>
        </div>
      </BNPModal>
    );
  }

  // Interstitials have their own component now
  if (placement === AdPlacement.INTERSTITIAL) {
    return null;
  }

  // 3. Regular Inline Placement Layouts - the box is constrained to the
  // placement's aspect ratio from the centralized dimensions config
  return (
    <div
      ref={adRef}
      data-ad-id={ad.id}
      data-tracked={impressionTracked}
      style={frameStyle}
      className={`
        relative w-full overflow-hidden rounded-lg border border-teal-100/50 
        bg-gradient-to-br from-teal-50/30 to-teal-100/20 mx-auto
        ${className}
      `.trim()}
    >
      {renderAdContent()}
    </div>
  );
};
